/*
 * Correctif WebKit (iPhone/iPad, tous navigateurs iOS) pour le picker du theme.
 * `markSelectedOption` (theme.js, fichier d'origine RoarTheme) retire l'attribut
 * `selected` PUIS lit `selectedOptions[0]`. Or WebKit, contrairement a Chrome,
 * reinitialise la selection d'un <select> sur sa PREMIERE option des que l'attribut
 * `selected` est retire d'une option choisie par programme — et ni
 * `option.selected = true` ni `selectedIndex` ne posent le drapeau « dirty » qui
 * l'empecherait (verifie sur WebKit). Consequence : apres un choix de telephone
 * dans la pop-in, le fetch de section partait avec la premiere option du select
 * trie — « Google Pixel 9 » — quel que soit le modele choisi. On capture donc
 * l'option choisie AVANT de toucher aux attributs, puis on retablit la selection
 * si WebKit l'a deplacee. theme.js etant un fichier d'origine, la methode est
 * remplacee ici plutot que corrigee sur place.
 */
customElements.whenDefined('variant-picker').then(() => {
  const VariantPicker = customElements.get('variant-picker');
  VariantPicker.prototype.markSelectedOption = function (target) {
    if (target.tagName !== 'SELECT' || !target.selectedOptions.length) return;
    const choisie = target.selectedOptions[0];
    Array.from(target.options).forEach((opt) => opt.removeAttribute('selected'));
    choisie.setAttribute('selected', '');
    if (target.selectedOptions[0] !== choisie) target.value = choisie.value;
  };
});

if (!customElements.get('phone-model-selector')) {
  customElements.define(
    'phone-model-selector',
    class PhoneModelSelector extends ModalElement {
      constructor() {
        super();

        this.models = null;
        this.loadPromise = null;
        this.currentBrand = null; // null = vue "marques" ; sinon = marque dont on affiche les modeles
      }

      get shouldLock() {
        return true;
      }

      get autoOpen() {
        return this.getAttribute('data-auto-open') === 'true';
      }

      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }

      get delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay')) : 2;
      }

      get expiry() {
        return this.hasAttribute('data-expiry') ? parseInt(this.getAttribute('data-expiry')) : 365;
      }

      get pages() {
        return this.hasAttribute('data-pages') ? parseInt(this.getAttribute('data-pages')) : 1;
      }

      get cookieName() {
        return 'phone_model';
      }

      get dismissedKey() {
        // Fermeture memorisee separement pour les collections : une fermeture sur l'index
        // n'empeche pas la modale de s'ouvrir sur une collection (et inversement). La modale
        // se propose ainsi "comme sur l'index" quand on arrive sur une collection sans modele.
        return window.location.pathname.includes('/collections/')
          ? 'phone-model:dismissed-collection'
          : 'phone-model:dismissed';
      }

      get cacheKey() {
        // Incrementer a chaque changement de source ou de forme des donnees : le cache vit
        // dans le sessionStorage, une liste tronquee y resterait sinon toute la session.
        return 'phone-model:models:v3';
      }

      get metaobjectType() {
        return this.getAttribute('data-metaobject-type') || '';
      }

      get storefrontToken() {
        return this.getAttribute('data-storefront-token') || '';
      }

      // Nom complet du parametre de facette (S&D). Le filtre n'est plus rendu dans les
      // facettes (plafond de 100 valeurs affichees pour ~500 modeles) : il s'applique
      // uniquement par l'URL, ou ce parametre reste interprete tant que le filtre existe
      // dans l'app Search & Discovery.
      get filterParamName() {
        return 'filter.p.m.custom.modeles_compatibles';
      }

      get optOutKey() {
        return 'phone-model:filter-optout';
      }

      /*
       * Collections ou l'auto-filtre modele ne doit PAS s'appliquer (reglage de section).
       *
       * Toutes les collections ne dependent pas du telephone : les accessoires (AirPods,
       * cables, supports voiture, grips...) n'ont pas de `modeles_compatibles` sur leurs
       * produits, forcer le filtre y vide le listing. La liste est un reglage plutot qu'une
       * heuristique sur le handle : `coques-etuis-pour-airpods` commence par « coque » sans
       * etre une coque de telephone.
       */
      get excludedCollectionHandles() {
        if (!this._excludedCollections) {
          this._excludedCollections = new Set(
            (this.getAttribute('data-excluded-collections') || '')
              .split(/[\s,;]+/)
              .map((entry) => this.collectionHandleOf(entry))
              .filter(Boolean)
          );
        }

        return this._excludedCollections;
      }

      // Handle de collection depuis un handle nu OU un chemin complet (`/fr/collections/x?y`) :
      // le reglage tolere ainsi un copier-coller d'URL, et le meme code sert aux liens.
      collectionHandleOf(value) {
        const raw = (value || '').toString().trim().toLowerCase();
        if (!raw) return '';

        const path = raw.split('?')[0].split('#')[0];
        const parts = path.split('/collections/');
        const tail = (parts.length > 1 ? parts[1] : path).split('/').filter(Boolean)[0] || '';

        try {
          return decodeURIComponent(tail);
        }
        catch {
          return tail;
        }
      }

      currentCollectionHandle() {
        if (!window.location.pathname.includes('/collections/')) return '';

        return this.collectionHandleOf(window.location.pathname);
      }

      // Hors collection (page recherche), rien a exclure : le filtre modele y reste pertinent.
      collectionAllowsModelFilter(handle) {
        if (!handle) return true;

        return !this.excludedCollectionHandles.has(handle);
      }

      get selection() {
        const raw = this.getCookie(this.cookieName);
        if (!raw) return null;

        try {
          return JSON.parse(decodeURIComponent(raw));
        }
        catch {
          return null;
        }
      }

      connectedCallback() {
        super.connectedCallback();

        this.input = this.querySelector('[data-phone-model-input]');
        this.results = this.querySelector('[data-results]');
        this.status = this.querySelector('[data-status]');

        this.input.addEventListener('input', () => this.renderList(this.input.value), { signal: this.abortController.signal });
        this.input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          this.results.querySelector('[data-handle], [data-brand]')?.click();
        }, { signal: this.abortController.signal });

        this.results.addEventListener('click', (event) => {
          // Clic sur une marque -> on affiche ses modeles.
          const brandButton = event.target.closest('[data-brand]');
          if (brandButton) {
            this.currentBrand = brandButton.getAttribute('data-brand');
            this.input.value = '';
            this.renderList('');
            return;
          }

          // Retour a la liste des marques.
          if (event.target.closest('[data-back]')) {
            this.currentBrand = null;
            this.input.value = '';
            this.renderList('');
            return;
          }

          const button = event.target.closest('[data-handle]');
          if (button) this.select(button.getAttribute('data-handle'));
        }, { signal: this.abortController.signal });

        // Revele les boutons du header (masques tant que la section n'est pas presente)
        document.querySelectorAll('.phone-model-button[hidden]').forEach((button) => button.removeAttribute('hidden'));
        this.updateButtons();

        this.initCollectionFilter();
        this.initSearchFilter();
        this.initSearchNotice();
        this.initProductHeading();
        this.initPickerLabels();
        this.initLinkDecoration();

        // Ouverture depuis le bouton « Changer » d'une fiche multi-marques : la liste se limite aux
        // modeles que CE produit propose (cf. visibleModels). En capture, pour etre pose avant que
        // le script de theme.liquid n'ouvre la modale.
        document.addEventListener('click', (event) => {
          if (event.target instanceof Element && event.target.closest('.cq-changer-telephone')) {
            this.restrictToProduct = true;
          }
        }, { capture: true, signal: this.abortController.signal });

        // Adoption avant `init()` : sinon la modale d'auto-ouverture pourrait se proposer
        // alors que la page vient justement de designer le modele.
        this.adoptCollectionModel()
          .then(() => this.adoptVariantModel())
          .finally(() => {
            // Apres l'adoption : sur une page modele, c'est le modele de la page qu'on veut voir
            // présélectionné (et filtré), pas l'ancien cookie.
            this.applyCollectionFilter();
            this.preselectProductVariant();
            this.init();
          });

        // Changement de modele depuis la modale alors qu'on est sur une fiche produit.
        // Choix EXPLICITE (modale) : il prime sur `?variant=`, que le theme ecrit dans l'URL des
        // le premier changement de variante. Les adoptions implicites (picker, variante d'URL)
        // gardent la garde : elles decrivent deja la variante affichee.
        document.addEventListener('phone-model:change', (event) => this.preselectProductVariant({ explicit: !event.detail?.implicit }), {
          signal: this.abortController.signal,
        });
      }

      disconnectedCallback() {
        super.disconnectedCallback();

        this.searchObserver?.disconnect();
        this.searchObserver = null;

        // Le pubsub du theme ne connait pas l'AbortSignal : desabonnement explicite.
        this.variantUnsubscribe?.();
        this.variantUnsubscribe = null;
      }

      // Recherche predictive (drawer et page) : l'API suggest de Shopify ne supporte pas les
      // filtres, on masque donc cote client les produits d'une AUTRE marque que le modele choisi
      // (chaque design/coque = un produit par marque, cf. `data-brand` = handleize(brand_name)).
      // Les produits sans marque (accessoires universels) restent visibles.
      // Les resultats sont re-rendus en innerHTML a chaque frappe -> MutationObserver (childList
      // uniquement : on ne mute que des attributs, pas de boucle d'observation).
      initSearchFilter() {
        this.searchForms = Array.from(document.querySelectorAll('form[is="predictive-search"]'));
        if (!this.searchForms.length) return;

        this.searchObserver = new MutationObserver(() => this.filterSearchResults());
        this.observeSearch();

        this.filterSearchResults();
      }

      observeSearch() {
        this.searchForms.forEach((form) => this.searchObserver.observe(form, { childList: true, subtree: true }));
      }

      filterSearchResults() {
        if (!this.searchObserver) return;

        // On coupe l'observer pendant nos propres ecritures DOM (append + textContent du message
        // "aucun resultat") : sinon elles re-declenchent l'observer -> boucle infinie -> onglet gele.
        this.searchObserver.disconnect();

        try {
          const model = this.selection;
          const modelBrand = model && model.marque ? this.normalize(model.marque) : null;

          document.querySelectorAll('form[is="predictive-search"]').forEach((form) => {
            // Les suggestions restent cadrees sur le modele memorise, comme la page de
            // resultats : le jeton de marque oriente la requete, ce masquage la rend stricte.
            // Sauf recherche visant un autre telephone, ou search.js n'a pas pose de jeton.
            const query = form.querySelector('input[name="q"]')?.value || '';
            const applyModel = Boolean(modelBrand) && !this.queryTargetsOtherBrand(query);

            // Le bandeau « Resultats pour votre <modele> » mentirait sur une recherche hors marque.
            const notice = form.querySelector('[data-phone-model-notice]');
            if (notice && model && model.nom) notice.hidden = !applyModel;

            form.querySelectorAll('.horizontal-product[data-brand]').forEach((item) => {
              if (!applyModel) {
                item.hidden = false;
                return;
              }

              const brand = this.normalize(item.getAttribute('data-brand'));
              // On ne masque que les produits d'une marque differente (brand vide = jamais masque).
              item.hidden = brand !== '' && brand !== modelBrand;
            });

            // Message quand toutes les suggestions produits (a marque) sont masquees
            form.querySelectorAll('.horizontal-products').forEach((grid) => {
              const annotated = grid.querySelectorAll('.horizontal-product[data-brand]');
              if (!annotated.length) return;

              const anyVisible = [...annotated].some((item) => !item.hidden);
              let message = grid.querySelector('.phone-model__search-empty');

              if (anyVisible || !applyModel) {
                if (message) message.hidden = true;
                return;
              }

              if (!message) {
                message = document.createElement('p');
                message.className = 'phone-model__search-empty text-sm text-opacity';
                message.append(document.createElement('span'), ' ');

                // Sortie de l'impasse : les produits d'une autre marque sont masques, on propose
                // de changer de telephone plutot que de laisser une liste vide sans issue.
                const change = document.createElement('button');
                change.type = 'button';
                change.className = 'link text-sm';
                change.textContent = 'Changer de téléphone';
                change.addEventListener('click', () => this.show());
                message.append(change);

                grid.append(message);
              }
              const text = `Aucun résultat pour votre ${this.modelDisplayName(model)}.`;
              const label = message.firstElementChild;
              if (label.textContent !== text) label.textContent = text;
              message.hidden = false;
            });
          });
        } finally {
          // On purge les mutations generees ci-dessus avant de reobserver.
          this.searchObserver.takeRecords();
          this.observeSearch();
        }
      }

      /*
       * Marques visees explicitement par une recherche, normalisees comme `data-brand`.
       *
       * Deux sources :
       *   - un nom de marque du catalogue, ou un alias courant (« galaxy » -> samsung) ;
       *   - un jeton contenant un chiffre (« a73 », « s24 », « 15 ») retrouve comme MOT
       *     ENTIER dans le `nom` d'un modele du catalogue -> la marque de ce modele.
       * Les modeles sont des metaobjets : « a73 » designe Galaxy A73 (Samsung) et Oppo A73
       * sans que l'internaute ait tape de marque.
       */
      brandsInQuery(query) {
        const words = this.normalize(query).split(/[^a-z0-9]+/).filter(Boolean);
        const brands = new Set();
        if (!words.length) return brands;

        const aliases = { iphone: 'apple', galaxy: 'samsung', pixel: 'google', redmi: 'xiaomi', poco: 'xiaomi' };
        const models = this.cachedModels() || [];
        const knownBrands = new Set(models.map((model) => this.normalize(model.marque)).filter(Boolean));

        words.forEach((word) => {
          if (aliases[word]) brands.add(aliases[word]);
          if (knownBrands.has(word)) brands.add(word);
        });

        // Une marque tapee prime sur les references : dans « redmi note 13 », le « 13 »
        // designerait aussi l'iPhone 13 et garderait a tort le cadrage d'un iPhone memorise.
        const references = brands.size ? [] : words.filter((word) => /\d/.test(word));
        if (references.length) {
          models.forEach((model) => {
            const nameWords = this.normalize(model.nom).split(/[^a-z0-9]+/);
            if (references.some((reference) => nameWords.includes(reference))) {
              brands.add(this.normalize(model.marque));
            }
          });
        }

        // Liste pas encore en memoire (debut de session, modale jamais ouverte) : on la charge
        // en arriere-plan pour les frappes suivantes ; d'ici la, seuls marques et alias comptent.
        if (!models.length && !this._modelsRequested) {
          this._modelsRequested = true;
          this.loadModels().catch(() => {});
        }

        return brands;
      }

      /*
       * Vrai quand la recherche vise une autre marque que le telephone memorise : le cadrage
       * est alors leve (pas de jeton cote search.js, pas de masquage ici). Une reference
       * ambigue qui inclut la marque memorisee (« 15 » = iPhone 15 et Xiaomi 15 pour un
       * iPhone) garde le cadrage : le doute profite au modele memorise.
       */
      queryTargetsOtherBrand(query) {
        const model = this.selection;
        const modelBrand = model && model.marque ? this.normalize(model.marque) : '';
        if (!modelBrand) return false;

        const brands = this.brandsInQuery(query);
        return brands.size > 0 && !brands.has(modelBrand);
      }

      /*
       * Bandeau « Résultats pour votre <modele> » sur la page de resultats.
       *
       * Rendu masque cote serveur : le modele vit dans le cookie, illisible en Liquid. Le
       * bouton est cable ici et non par `aria-controls` : overlay-group rend la modale AVANT
       * le contenu de page, son `connectedCallback` ne voit donc pas ce bouton.
       *
       * Changer de telephone depuis ce bandeau relance la recherche : la page a ete
       * interrogee avec le cadrage marque de l'ANCIEN modele, la recharger est le seul moyen
       * d'obtenir des resultats cadres sur le nouveau.
       */
      initSearchNotice() {
        this.searchNotices = Array.from(document.querySelectorAll('[data-phone-model-notice]'));
        if (!this.searchNotices.length) return;

        this.searchNotices.forEach((notice) => {
          notice
            .querySelector('[data-phone-model-notice-change]')
            ?.addEventListener('click', () => this.show(), { signal: this.abortController.signal });
        });

        this.renderSearchNotice();
        document.addEventListener('phone-model:change', () => {
          this.renderSearchNotice();
          this.reloadSearchForModel();
        }, { signal: this.abortController.signal });
      }

      /*
       * H1 de la fiche produit enrichi du modele choisi.
       *
       * Le titre porte la MARQUE, pas le modele (« Coque iPhone Panda Bubble », « Coque Samsung
       * Oracle »). On remplace ce jeton de marque par le nom d'affichage du modele :
       *
       *   nom du modele s'il commence deja par la marque   Apple / iPhone 15 Pro -> « iPhone 15 Pro »
       *   sinon « marque + nom »                           Samsung / Galaxy S24  -> « Samsung Galaxy S24 »
       *
       * -> « Coque iPhone 15 Pro Panda Bubble », « Coque Samsung Galaxy S24 Oracle ».
       *
       * Les deux regles naives echouent chacune d'un cote : remplacer betement la marque perd
       * « Samsung », inserer apres la marque donne « Coque iPhone iPhone 15 Pro ». Meme cas
       * particulier Apple que le cadrage de recherche : aucun titre ne contient « Apple ».
       *
       * Titre affiche seulement — ni <title>, ni metadonnees.
       */
      /*
       * Libelles du select « Modeles compatibles » : la valeur d'option reprend le `nom` du
       * metaobject, souvent sans la marque (Xiaomi « 13 », OnePlus « 9 »). Le Liquid le
       * corrige quand la variante porte le metafield (snippet `modele-libelle`) ; sinon
       * — cas du verre trempe integral, lie au metaobject par l'OPTION seule, lien que le
       * Liquid n'expose pas — on complete ici avec la liste complete des modeles.
       *
       * Seul le texte change, jamais la `value` (choix de variante, panier). Un nom partage
       * par plusieurs modeles est laisse tel quel plutot que de risquer la mauvaise marque.
       * Rejoue a chaque changement de variante : le theme reinsere le picker a chaque fois.
       */
      initPickerLabels() {
        if (!document.querySelector('variant-picker select[data-sort-options]')) return;

        this.renderPickerLabels();
        this.pickerLabelsUnsubscribe = theme.pubsub?.subscribe?.(
          theme.pubsub.PUB_SUB_EVENTS.variantChange,
          () => this.renderPickerLabels()
        );
      }

      async renderPickerLabels() {
        let models;
        try {
          models = await this.loadModels();
        }
        catch {
          return;
        }
        if (!models || !models.length) return;

        if (!this._modelsByName) {
          this._modelsByName = new Map();
          models.forEach((model) => {
            const key = this.normalize(model.nom);
            if (!key) return;
            this._modelsByName.set(key, [...(this._modelsByName.get(key) || []), model]);
          });
        }

        document.querySelectorAll('variant-picker select[data-sort-options]').forEach((select) => {
          let changed = false;
          Array.from(select.options).forEach((option) => {
            const matches = this._modelsByName.get(this.normalize(option.value));
            if (!matches || matches.length !== 1) return;
            const label = this.modelDisplayName(matches[0]);
            if (label && option.textContent.trim() !== label) {
              option.textContent = label;
              changed = true;
            }
          });

          // Valeur affichee a cote du nom de l'option (« Modeles compatibles : 13 »).
          const selected = select.options[select.selectedIndex];
          const current = select.closest('.variant-input-wrapper')?.querySelector('.form__label span.font-medium');
          if (selected && current && current.textContent.trim() !== selected.textContent.trim()) {
            current.textContent = selected.textContent.trim();
          }

          if (changed) select.closest('variant-picker')?.sortSelectOptions?.();
        });
      }

      initProductHeading() {
        if (!document.querySelector('[data-product-title][data-title-base]')) return;

        this.renderProductHeading();

        document.addEventListener('phone-model:change', () => this.renderProductHeading(), {
          signal: this.abortController.signal,
        });
        /*
         * Changement de variante dans le picker. En CAPTURE et sur `document` : le theme
         * remplace le picker par la version serveur a chaque changement de variante, un
         * listener pose sur l'element d'origine mourrait au premier remplacement.
         */
        document.addEventListener('change', (event) => {
          if (!(event.target instanceof Element) || !event.target.closest('variant-picker')) return;

          /*
           * Choisir un modèle dans le picker est un choix explicite : on le mémorise, comme
           * une sélection faite dans la modale. Sans ça le H1 afficherait le modèle choisi
           * pendant que le bouton d'en-tête resterait sur l'ancien.
           *
           * On rend le H1 dans tous les cas plutôt que de se fier à l'événement émis par
           * `adoptModel` : celui-ci ne part que si le modèle mémorisé change vraiment, or
           * le H1 suit le picker, qui peut bouger sans que le cookie bouge (bascule sur le
           * modèle déjà mémorisé). Le rendu est idempotent — il compare le texte avant
           * d'écrire — donc le doublon avec `phone-model:change` ne coûte rien.
           */
          this.modelFromPicker().then((model) => {
            this.adoptModel(model);
            this.renderProductHeading();
          });
        }, { capture: true, signal: this.abortController.signal });

        /*
         * Filet apres le rendu serveur : `updateOptionValues` peut deplacer la selection
         * si le modele courant devient indisponible pour le type choisi. Le `change` de
         * l'internaute a alors deja ete traite, avec l'ancienne valeur.
         *
         * On passe par le pubsub du theme et non par `variant:change` : cet evenement est
         * emis sur le formulaire produit sans `bubbles`, il n'atteint donc pas `document`.
         */
        this.variantUnsubscribe = theme.pubsub?.subscribe?.(
          theme.pubsub.PUB_SUB_EVENTS.variantChange,
          () => this.renderProductHeading()
        );
      }

      // Jeton de marque tel qu'il figure dans les titres produit.
      titleTokenFor(model) {
        const marque = (model && model.marque ? model.marque : '').trim();
        if (!marque) return '';
        return this.normalize(marque) === 'apple' ? 'iPhone' : marque;
      }

      // Nom affiche d'un modele : le champ `nom` du metaobject omet parfois la marque
      // (Xiaomi « 13 », Oppo « A5 2020 ») ; on la prefixe, « iPhone » tenant lieu d'Apple.
      // Utilise partout ou un modele est affiche (H1, pop-in, libelle du header, recherche).
      modelDisplayName(model) {
        const nom = (model && model.nom ? model.nom : '').trim();
        const token = this.titleTokenFor(model);
        if (!nom || !token) return nom;
        return this.normalize(nom).startsWith(this.normalize(token)) ? nom : `${token} ${nom}`;
      }

      /*
       * Le H1 suit le modele CHOISI dans le picker, pas celui memorise en cookie : c'est
       * la demande ("afficher les noms des produits avec le modele de coque choisi"), et
       * les deux divergent des que l'internaute change le select « Modeles compatibles ».
       * Repli sur le cookie quand le picker ne dit rien (pas de picker, liste indisponible).
       */
      async renderProductHeading() {
        // Requete a chaque rendu, jamais mis en cache : `renderProductInfo` (theme.js)
        // remplace le bloc produit apres un changement de variante, une reference gardee
        // pointerait sur un noeud detache.
        const heading = document.querySelector('[data-product-title][data-title-base]');
        if (!heading) return;

        // Les changements s'enchainent plus vite que le chargement de la liste des modeles :
        // on abandonne un rendu que le suivant a deja rendu obsolete.
        const jeton = (this._headingSeq = (this._headingSeq || 0) + 1);

        const base = heading.getAttribute('data-title-base') || '';
        // Le H1 doit suivre uniquement un modele reellement present dans le picker
        // de variantes de la fiche produit.
        //
        // Le modele memorise dans la pop-in du header ne doit PAS etre utilise ici :
        // un accessoire universel (cable, chargeur, support...) peut avoir un telephone
        // selectionne dans la pop-in sans posseder de variante "Modele compatible".
        const model = await this.modelFromPicker();
        if (jeton !== this._headingSeq) return;
        // Aucun modele trouve dans les variantes de cette fiche :
        // on conserve/restaure simplement le titre Shopify d'origine.
        if (!model) {
          const target = heading.querySelector('split-words') || heading;
          if (target.textContent.trim() !== base.trim()) {
            target.textContent = base;
          }
          return;
        }

        const token = this.titleTokenFor(model);
        let text = base;

      if (base && token) {
        // Jeton isole, insensible a la casse et aux accents. Sans les bornes de mot,
        // « LG » se retrouverait a l'interieur de n'importe quel mot.
        const pattern = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const match = new RegExp(`(^|[^\\p{L}\\p{N}])(${pattern})(?=[^\\p{L}\\p{N}]|$)`, 'iu').exec(base);

        const display = this.modelDisplayName(model);

        if (match) {
          // Cas ou la marque est deja presente dans le titre produit :
          // « Coque Samsung Flower wall »
          // devient « Coque Samsung Galaxy A34 5G Flower wall ».
          //
          // On remplace uniquement le jeton de marque par le nom complet du modele,
          // sans modifier le reste du titre.
          text = base.slice(0, match.index) + match[1] + display + base.slice(match.index + match[0].length);
        }
        else if (display) {
          // Cas ou le titre produit ne contient aucune marque :
          // « Coque en silicone Soft Touch Noir »
          // devient « Coque en silicone Soft Touch Noir – Samsung Galaxy A02S ».
          //
          // Cela permet d'enrichir aussi les produits standards, folios, verres trempes,
          // accessoires, etc., tout en conservant leur titre produit d'origine intact.
          text = `${base} – ${display}`;
        }
      }

        // `split-words` n'est present que si le reveal de titre est actif dans les reglages.
        const target = heading.querySelector('split-words') || heading;
        if (target.textContent.trim() !== text.trim()) target.textContent = text;
      }

      // Uniquement sur la page de resultats : elle a ete interrogee avec le cadrage de
      // l'ANCIEN modele, seule une recharge peut la recadrer. Ailleurs (tiroir de recherche
      // ouvert sur une fiche produit, une collection...) changer de telephone ne doit pas
      // faire naviguer l'internaute.
      reloadSearchForModel() {
        if (!window.location.pathname.includes('/search')) return;

        const form = document.querySelector('form[is="predictive-search"]');
        if (!form || typeof form.searchUrlFor !== 'function') return;

        const input = form.querySelector('input[type="search"]');
        const url = form.searchUrlFor(input ? input.value : '');
        if (url) window.location.assign(url);
      }

      renderSearchNotice() {
        if (!this.searchNotices) return;

        const model = this.selection;
        // Page de resultats d'une recherche visant un autre telephone : search.js n'a pas cadre
        // la requete, le bandeau annoncerait a tort des resultats « pour votre <modele> ».
        const pageQuery = window.location.pathname.includes('/search')
          ? new URLSearchParams(window.location.search).get('q') || ''
          : '';
        const otherBrandPage = Boolean(pageQuery) && this.queryTargetsOtherBrand(pageQuery);

        this.searchNotices.forEach((notice) => {
          if (!model || !model.nom || (otherBrandPage && !notice.closest('form'))) {
            notice.hidden = true;
            return;
          }

          const name = notice.querySelector('[data-phone-model-notice-name]');
          if (name) name.textContent = this.modelDisplayName(model);
          notice.hidden = false;
        });
      }

      /*
       * Pages ou l'auto-filtre modele peut s'appliquer : les COLLECTIONS uniquement.
       *
       * Shopify n'expose pas les filtres de metachamp produit sur /search : la page ne
       * remonte que `color-pattern`, `availability` et `price` dans `search.filters`,
       * la ou une collection en remonte 5 dont `modeles_compatibles`. Et le parametre
       * n'y est pas ignore : passe dans l'URL de recherche, il vide le resultat (0
       * produit pour n'importe quel terme, meme un produit reellement compatible).
       * On ne l'applique donc jamais en recherche.
       */
      isFilterablePage() {
        return window.location.pathname.includes('/collections/');
      }

      initCollectionFilter() {
        if (!this.isFilterablePage()) return;
        if (!document.querySelector('form[is="facet-form"]')) return;

        // Le filtre modele n'a pas de pastille (il se gere via la modale) : seul
        // "tout effacer" peut encore le retirer par un lien facet-remove — on memorise
        // alors l'intention pour la session, sinon l'auto-filtre le reposerait aussitot.
        // En CAPTURE : le listener de FacetRemove (niveau cible) fait un replaceState
        // synchrone qui retire deja le parametre de l'URL — en phase de bulle on lirait
        // une URL sans filtre modele et on ne poserait jamais l'opt-out.
        document.addEventListener('click', (event) => {
          const link = event.target instanceof Element ? event.target.closest('a[is="facet-remove"]') : null;
          if (!link) return;

          const model = this.selection;
          if (!model) return;

          const active = this.activeModelFilterValues();
          if (!active.some((value) => this.valueMatchesModel(value, model))) return;

          const target = new URL(link.href, window.location.origin);
          const stillActive = target.searchParams.getAll(this.filterParamName)
            .some((value) => this.valueMatchesModel(value, model));
          if (!stillActive) sessionStorage.setItem(this.optOutKey, '1');
        }, { capture: true, signal: this.abortController.signal });

        // A l'init puis apres chaque rendu AJAX des facettes (l'evenement est dispatche
        // par FacetForm.afterRenderSection, qui vient de remplacer blocs et pastilles).
        this.syncModelFilterState();
        document.addEventListener('collection:reloaded', () => this.syncModelFilterState(), {
          signal: this.abortController.signal,
        });

        // `applyCollectionFilter` n'est PAS appele ici mais apres `adoptCollectionModel` (cf.
        // connectedCallback) : a l'arrivee sur une collection de modele sans cookie (annonce
        // Google, navigation privee), la selection n'existe pas encore et le filtre ne
        // s'appliquait qu'au F5 ; avec le cookie d'un autre modele, c'est l'ANCIEN qui partait.
      }

      // Valeurs actives du filtre modele, lues dans l'URL (le rendu AJAX des facettes
      // maintient l'URL a jour via history.replaceState).
      activeModelFilterValues() {
        return new URLSearchParams(window.location.search).getAll(this.filterParamName);
      }

      valueMatchesModel(value, model) {
        // La valeur du filtre metafield (list.metaobject_reference) est le GID du metaobject
        if (!value || !model || !model.id) return false;
        return String(value).split('/').pop() === String(model.id).split('/').pop();
      }

      // Remet un id de metaobjet sous forme de GID, seule forme acceptee par le filtre
      // `filter.p.m.custom.modeles_compatibles`. Couvre les cookies poses avant la
      // correction du repli Liquid, qui contiennent l'id numerique de `system.id`.
      toMetaobjectGid(value) {
        if (!value) return null;
        const raw = String(value);
        if (raw.startsWith('gid://')) return raw;
        return /^\d+$/.test(raw) ? `gid://shopify/Metaobject/${raw}` : null;
      }

      // GID du modele selectionne. Les cookies poses avant l'ajout du champ `id` n'en ont
      // pas : on complete alors par un lookup par handle et on reecrit le cookie.
      async ensureSelectionGid() {
        const model = this.selection;
        if (!model) return null;

        const gid = this.toMetaobjectGid(model.id);
        if (gid) return gid;

        const fetched = await this.fetchModelByHandle(model.handle);
        if (!fetched) return null;

        this.setCookie(this.cookieName, encodeURIComponent(JSON.stringify(fetched)), this.expiry);
        return fetched.id;
      }

      applyCollectionFilter({ force = false } = {}) {
        const model = this.selection;
        if (!model) return;
        if (!force && sessionStorage.getItem(this.optOutKey)) return;
        if (!this.isFilterablePage()) return;
        // Collection exclue : on ne pose rien, mais `syncModelFilterState` continue de tenir
        // les inputs a jour si le filtre est deja dans l'URL (lien partage).
        if (!this.collectionAllowsModelFilter(this.currentCollectionHandle())) return;
        if (!document.querySelector('form[is="facet-form"]')) return;

        customElements.whenDefined('facet-form').then(async () => {
          const gid = await this.ensureSelectionGid();
          if (!gid) return;

          const active = this.activeModelFilterValues();

          if (force) {
            // Deja filtre sur le bon modele (arrivee via un lien decore) : rien a recharger.
            if (active.length === 1 && this.valueMatchesModel(active[0], model)) return;
          }
          else {
            // Un filtre modele est deja actif (arrivee via une URL filtree) : on n'y touche pas
            if (active.length) return;
          }

          this.dispatchFacetChange(this.injectModelFilterInputs([gid]));
        });
      }

      // Pose les valeurs du filtre en <input hidden> dans CHAQUE form de facettes (sidebar,
      // drawer, sticky) : FacetForm.buildUrl serialise le form au prochain changement, le
      // filtre modele doit donc exister dans chacun. Renvoie un input d'ou dispatcher le
      // `change` qui declenche le pipeline AJAX du theme.
      injectModelFilterInputs(values) {
        let dispatchTarget = null;

        document.querySelectorAll('form[is="facet-form"]').forEach((form) => {
          form.querySelectorAll(`input[name="${this.filterParamName}"]`).forEach((el) => el.remove());

          const host = form.querySelector('[data-filter][data-index$="-page"]') || form;
          values.forEach((value) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = this.filterParamName;
            input.value = value;
            input.setAttribute('data-model-filter', '');

            host.append(input);
            dispatchTarget = dispatchTarget || input;
          });
        });

        return dispatchTarget;
      }

      /*
       * Resynchronisation des inputs hidden du filtre modele depuis l'URL.
       *
       * S&D ne remonte dans `filter.active_values` que les valeurs de son top 100 : pour
       * un modele au-dela, le filtre s'applique (le listing est bien reduit) mais le rendu
       * serveur n'emet pas les inputs hidden. Sans eux, le prochain changement de facette
       * serialiserait le form sans le modele (le filtre "saute"). L'URL fait donc foi :
       * on reconstruit les inputs a l'init et apres chaque rendu AJAX
       * (`collection:reloaded`), qui remplace les blocs de facettes par la version serveur.
       *
       * Pas de pastille pour ce filtre (ni serveur — facets-active.liquid le saute — ni
       * injectee) : le modele se gere uniquement via la modale de selection.
       */
      syncModelFilterState() {
        const values = this.activeModelFilterValues();
        if (!values.length) return;

        this.injectModelFilterInputs(values);
      }

      dispatchFacetChange(input) {
        if (!input) return;

        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      init() {
        if (Shopify.designMode) return;

        if (this.testMode) return this.load();
        if (!this.autoOpen) return;
        if (this.selection) return;
        if (sessionStorage.getItem(this.dismissedKey)) return;

        this.load();
      }

      load() {
        setTimeout(() => this.show(), this.delay * 1000);
      }

      beforeShow() {
        super.beforeShow();

        // Reouverture : on repart de la vue "marques".
        this.currentBrand = null;
        if (this.input) this.input.value = '';

        this.setStatus('Chargement des modèles…');
        this.loadModels()
          .then(() => {
            this.setStatus(null);
            this.renderList(this.input.value);
          })
          .catch(() => this.setStatus('Impossible de charger les modèles. Veuillez réessayer.'));
      }

      afterShow() {
        super.afterShow();

        if (window.matchMedia('(min-width: 640px)').matches) {
          this.input.focus();
        }
      }

      afterHide() {
        super.afterHide();
        this.restrictToProduct = false;

        // Ferme sans choisir : on ne re-propose plus la modale durant cette session
        if (!this.testMode && !this.selection) {
          sessionStorage.setItem(this.dismissedKey, '1');
        }
      }

      get collectionModel() {
        return (this.getAttribute('data-collection-model') || '').trim();
      }

      handleizeValue(value) {
        return this.normalize(value).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      }

      /*
       * Les handles des collections de modele suivent DEUX conventions :
       *   - le handle du metaobjet, prefixe par la marque : `coque-oppo-find-x3-lite`
       *   - le `nom` seul, sans marque : `coque-iphone-17-pro`
       * Le champ `nom` ne portant que le modele (« Find X3 Lite », « Galaxy S21 »), comparer
       * au seul `nom` ratait tous les handles prefixes. On teste les deux, handle d'abord :
       * il est sans ambiguite, alors qu'un `nom` peut se repeter d'une marque a l'autre.
       */
      matchesCollectionModel(model, target) {
        if (!model || !target) return false;
        return this.handleizeValue(model.handle) === target
          || this.handleizeValue(model.nom) === target;
      }

      // Liste des modeles sans requete : memoire ou cache sessionStorage (rempli par un
      // passage anterieur dans la modale). Peut renvoyer null en debut de session.
      cachedModels() {
        if (this.models) return this.models;

        try {
          const cached = sessionStorage.getItem(this.cacheKey);
          if (cached) this.models = JSON.parse(cached);
        }
        catch { }

        return this.models;
      }

      isModelCollectionHandle(handle) {
        const models = this.cachedModels();
        // Liste indisponible : on decore quand meme, adoptCollectionModel corrigera a l'arrivee.
        if (!models) return false;

        // Meme normalisation que `data-collection-model` cote Liquid
        // (`collection.handle | remove_first: 'coque-'`), que matchesCollectionModel attend.
        const target = handle.replace('coque-', '');
        return models.some((model) => this.matchesCollectionModel(model, target));
      }

      /*
       * Decoration des liens vers les collections : le filtre modele s'applique par l'URL
       * (le groupe de facettes n'est plus rendu), on ajoute donc le parametre au moment de
       * l'interaction. `pointerdown` reecrit le href AVANT que le navigateur ne le lise,
       * ce qui couvre clic, ctrl/cmd+clic, clic molette et "ouvrir dans un nouvel onglet" ;
       * `keydown` couvre la navigation clavier. Pas de reecriture globale du DOM : rien a
       * resynchroniser apres les rendus AJAX (facettes, recherche predictive, sections).
       */
      initLinkDecoration() {
        const decorate = (event) => {
          const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
          if (link) this.decorateCollectionLink(link);
        };

        document.addEventListener('pointerdown', decorate, { capture: true, signal: this.abortController.signal });
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') decorate(event);
        }, { capture: true, signal: this.abortController.signal });
      }

      decorateCollectionLink(link) {
        const model = this.selection;
        if (!model || !model.id) return;
        if (sessionStorage.getItem(this.optOutKey)) return;
        // Les pastilles de retrait sont des liens de collection : les decorer re-appliquerait
        // le filtre qu'elles retirent.
        if (link.getAttribute('is') === 'facet-remove') return;
        if (!(link.getAttribute('href') || '').includes('/collections/')) return;

        let url;
        try {
          url = new URL(link.href, window.location.origin);
        }
        catch {
          return;
        }

        if (url.origin !== window.location.origin) return;
        if (!url.pathname.includes('/collections/')) return;
        if (url.searchParams.has(this.filterParamName)) return;

        // Collection dediee a un modele (`coque-iphone-17-pro`...) : on ne filtre pas, la
        // page designe elle-meme son modele (adoptCollectionModel l'adoptera a l'arrivee).
        const handle = this.collectionHandleOf(url.pathname);
        if (!handle || this.isModelCollectionHandle(handle)) return;
        // Collection exclue (accessoires) : le lien reste nu, la page n'a pas de modele.
        if (!this.collectionAllowsModelFilter(handle)) return;

        url.searchParams.append(this.filterParamName, model.id);
        link.setAttribute('href', url.pathname + url.search + url.hash);
      }

      /*
       * Lookup direct du metaobjet par handle, sans passer par la liste.
       *
       * La liste chargee par `loadModels()` peut etre incomplete (repli Liquid plafonne, ou
       * pagination interrompue) : chercher dedans echouait sur `oppo-find-x3-lite` alors que
       * le metaobjet existe. Un `metaobject(handle:)` est O(1) et ne peut pas etre tronque.
       */
      async fetchModelByHandle(handle) {
        const token = this.storefrontToken;
        const type = this.metaobjectType;
        if (!token || !type || !handle) return null;

        const query = `
          query ModelByHandle($handle: MetaobjectHandleInput!) {
            metaobject(handle: $handle) {
              id
              handle
              fields { key value }
            }
          }
        `;

        try {
          const response = await fetch('/api/2024-10/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': token,
            },
            body: JSON.stringify({ query, variables: { handle: { type, handle } } }),
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const payload = await response.json();
          if (payload.errors) throw new Error(JSON.stringify(payload.errors));

          const node = payload?.data?.metaobject;
          if (!node) return null;

          const byKey = {};
          (node.fields || []).forEach((field) => byKey[field.key] = field.value);
          if (!byKey.nom) return null;

          return { id: node.id, handle: node.handle, nom: byKey.nom, marque: byKey.marque || '' };
        }
        catch (error) {
          console.warn('[phone-model] lookup par handle impossible :', error.message);
          return null;
        }
      }

      /*
       * Adoption implicite du modele de la page.
       *
       * Arriver sur la collection d'un modele precis (Marques > Apple > iPhone 12) est un choix
       * plus explicite que le cookie memorise. Sans ca, `collection-brand-suggestion` proposait
       * de QUITTER la page pour revenir au modele precedent — exactement l'inverse de l'intention.
       *
       * Le filtre modele s'applique ensuite (connectedCallback) : une collection de modele
       * (`coque-google-pixel-7`) liste aussi des produits d'autres modeles de la marque.
       */
      async adoptCollectionModel() {
        const target = this.collectionModel;
        if (!target || this.testMode || this._adopted) return null;
        this._adopted = true;

        if (this.matchesCollectionModel(this.selection, target)) return this.selection;

        // 1. Le handle de collection est le handle du metaobjet (`coque-oppo-find-x3-lite`).
        let model = await this.fetchModelByHandle(target);

        // 2. Sinon le handle reprend le `nom` seul (`coque-iphone-17-pro`) : la liste tranche.
        if (!model) {
          let models;
          try {
            models = await this.loadModels();
          }
          catch {
            return null; // liste indisponible : on laisse la selection en place
          }
          model = models?.find((item) => this.handleizeValue(item.handle) === target)
            || models?.find((item) => this.handleizeValue(item.nom) === target);
        }

        if (!model) return null;

        this.setCookie(this.cookieName, encodeURIComponent(JSON.stringify(model)), this.expiry);
        this.updateButtons();
        // Choix assume, comme une selection explicite : on reactive l'auto-filtre pour la suite
        // de la navigation.
        sessionStorage.removeItem(this.optOutKey);

        document.dispatchEvent(new CustomEvent('phone-model:change', {
          bubbles: true,
          detail: { model, implicit: true },
        }));

        // Un lien decore avec l'ANCIEN modele a echappe a l'exclusion (liste des modeles pas
        // encore en cache au moment du clic) : la page rend alors ce modele filtre par un
        // autre, quasi toujours 0 resultat. On remplace le filtre errone par le modele adopte.
        const active = this.activeModelFilterValues();
        if (active.length && !active.every((value) => this.valueMatchesModel(value, model))) {
          this.applyCollectionFilter({ force: true });
        }

        return model;
      }

      /*
       * Presélection de la variante correspondant au modele choisi, sur toute fiche produit.
       *
       * `model-brand-suggestion` le fait deja, mais son snippet exige brand_name + design_name
       * + un handle en `coque-` : les produits universels (verre trempe, accessoires
       * multi-marques) n'ont donc AUCUNE présélection, et leur dropdown reste sur la premiere
       * valeur de la liste — « Alcatel 1S 2020 ». Ici on ne depend que de la presence d'une
       * option portant le nom du modele.
       *
       * Le theme (VariantPicker + ProductInfo) prend le relais sur l'evenement `change` :
       * URL ?variant, prix, galerie, masques.
       */
      /*
       * Les valeurs d'option ne nomment pas le modele de la meme facon selon la gamme :
       *   - coques design, option « Modeles compatibles » : le `nom` seul  -> « iPhone 11 »
       *   - autres produits, option « Telephone » : marque + nom          -> « Samsung Galaxy S21 »
       * On teste donc les deux formes. Et certaines valeurs couvrent deux modeles
       * (« Apple iPhone X / XS », « Oppo Find X3 / X3 Pro ») : on accepte alors le premier
       * segment, ce qui rattrape le modele principal sans risquer un faux positif sur un
       * simple prefixe (« Galaxy A2 » ne doit pas matcher « Galaxy A20 »).
       */
      variantValueMatches(value, candidates) {
        const normalized = this.normalize(value);
        if (candidates.includes(normalized)) return true;
        if (!normalized.includes('/')) return false;
        return candidates.includes(this.normalize(normalized.split('/')[0]));
      }

      /*
       * Adoption du modele porte par la variante de l'URL (fiche produit ouverte par un lien
       * direct `?variant=…`, cas des annonces Google).
       *
       * Miroir de `adoptCollectionModel` : la page designe un modele, plus explicite que le
       * cookie. Sans ca la bonne coque s'affichait bien — `preselectProductVariant` s'efface
       * devant `?variant=` — mais le telephone memorise en haut restait sur l'ancien, et
       * `model-brand-suggestion` pouvait proposer de changer pour le modele deja affiche.
       *
       * On lit la variante rendue par le serveur dans le picker plutot que de resoudre l'id :
       * avec jusqu'a 2048 variantes par produit, aucune liste exhaustive n'est exposee au JS.
       * Les autres options ne risquent pas la collision — « Standard », « Renforcee »,
       * « Argent », « — » ne sont des noms de modele pour personne.
       */
      // Valeurs d'options actuellement selectionnees dans le picker (selects + radios).
      pickerValues() {
        const picker = document.querySelector('variant-picker');
        if (!picker) return [];

        const values = [];
        picker.querySelectorAll('select').forEach((select) => select.value && values.push(select.value));
        picker.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => values.push(input.value));
        return values;
      }

      modelCandidates(model) {
        const candidates = [this.normalize(model.nom)];
        if (model.marque) candidates.push(this.normalize(model.marque + ' ' + model.nom));
        return candidates;
      }

      /*
       * Modele de telephone porte par la variante actuellement selectionnee.
       *
       * Sert a l'adoption a l'arrivee (`?variant=`) ET au H1, qui doit suivre le select
       * « Modeles compatibles » : le H1 affiche le modele CHOISI, pas celui memorise en
       * cookie. On teste la selection en cookie d'abord, ce qui evite de charger la liste
       * des modeles dans le cas courant ou les deux coincident.
       */
      async modelFromPicker() {
        const values = this.pickerValues();
        if (!values.length) return null;

        const current = this.selection;
        if (current && current.nom) {
          const candidates = this.modelCandidates(current);
          if (values.some((value) => this.variantValueMatches(value, candidates))) return current;
        }

        let models;
        try {
          models = await this.loadModels();
        }
        catch {
          return null; // liste indisponible : on laisse la selection en place
        }
        if (!models) return null;

        return models.find((item) =>
          values.some((value) => this.variantValueMatches(value, this.modelCandidates(item)))) || null;
      }

      /*
       * Mémorise un modèle choisi implicitement par la page (collection modèle, variante
       * d'URL, select du picker) : cookie, libellé du bouton d'en-tête, et notification.
       *
       * Comparaison sur le `handle` et non par identité : `selection` reparse le cookie et
       * rend un objet neuf à chaque appel, `===` serait toujours faux et on réécrirait le
       * cookie pour rien à chaque changement de variante.
       */
      adoptModel(model) {
        if (!model) return null;
        if (this.selection && this.selection.handle === model.handle) return model;

        this.setCookie(this.cookieName, encodeURIComponent(JSON.stringify(model)), this.expiry);
        this.updateButtons();
        // Choix assume, comme sur une collection de modele : l'auto-filtre reprend ensuite.
        sessionStorage.removeItem(this.optOutKey);

        document.dispatchEvent(new CustomEvent('phone-model:change', {
          bubbles: true,
          detail: { model, implicit: true },
        }));

        return model;
      }

      async adoptVariantModel() {
        if (this.testMode || this._adoptedVariant) return null;
        if (!new URLSearchParams(window.location.search).has('variant')) return null;
        if (!document.querySelector('variant-picker')) return null;
        this._adoptedVariant = true;

        return this.adoptModel(await this.modelFromPicker());
      }

      preselectProductVariant({ explicit = false } = {}) {
        const model = this.selection;
        if (!model || !model.nom) return false;
        // Variante imposee par l'URL (lien partage, arrivee via une suggestion) : on n'y touche
        // pas au chargement. Un choix fait dans la modale, lui, doit changer la variante.
        if (!explicit && new URLSearchParams(window.location.search).has('variant')) return false;

        const picker = document.querySelector('variant-picker');
        if (!picker) return false;

        const candidates = [this.normalize(model.nom)];
        if (model.marque) candidates.push(this.normalize(model.marque + ' ' + model.nom));

        for (const select of picker.querySelectorAll('select')) {
          const option = Array.from(select.options).find((opt) => this.variantValueMatches(opt.value, candidates));
          if (!option) continue;
          if (select.value === option.value) return true; // deja selectionne
          // `selected` en attribut aussi : le custom-select du theme lit le DOM pour son libelle.
          Array.from(select.options).forEach((opt) => opt.removeAttribute('selected'));
          option.setAttribute('selected', '');
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }

        for (const input of picker.querySelectorAll('fieldset input[type="radio"]')) {
          if (!this.variantValueMatches(input.value, candidates)) continue;
          if (input.checked) return true;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }

        return false;
      }

      loadModels() {
        if (this.models) return Promise.resolve(this.models);
        if (this.loadPromise) return this.loadPromise;

        const cached = sessionStorage.getItem(this.cacheKey);
        if (cached) {
          try {
            this.models = JSON.parse(cached);
            return Promise.resolve(this.models);
          }
          catch {
            sessionStorage.removeItem(this.cacheKey);
          }
        }

        this.loadPromise = this.fetchModels()
          .then((models) => {
            this.models = models;

            try {
              sessionStorage.setItem(this.cacheKey, JSON.stringify(models));
            }
            catch { }

            return models;
          })
          .catch((error) => {
            // Journalise puis repropage : beforeShow affiche le message d'erreur. Rien n'est
            // mis en cache et this.models reste null, la prochaine ouverture retente donc.
            console.warn('[phone-model] chargement des modeles impossible :', error);
            throw error;
          })
          .finally(() => this.loadPromise = null);

        return this.loadPromise;
      }

      // Liquid n'expose que les 250 premieres entrees du metaobjet : `paginate ... by 250`
      // rend bien 250 lignes, mais les pages suivantes sont vides et la Section Rendering API
      // ne va pas plus loin. La Storefront API, paginee par curseur, est la seule source
      // exhaustive (meme contournement que compatible-mask.js pour le plafond des metafields).
      async fetchModelsViaStorefrontAPI() {
        const token = this.storefrontToken;
        const type = this.metaobjectType;
        if (!token || !type) return null;

        const query = `
          query GetPhoneModels($type: String!, $cursor: String) {
            metaobjects(type: $type, first: 250, after: $cursor) {
              edges {
                node {
                  id
                  handle
                  fields { key value }
                }
              }
              pageInfo { hasNextPage endCursor }
            }
          }
        `;

        const models = [];
        let cursor = null;
        let safety = 0;
        while (safety++ < 40) {
          const response = await fetch('/api/2024-10/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': token,
            },
            body: JSON.stringify({ query, variables: { type, cursor } }),
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const payload = await response.json();
          if (payload.errors) throw new Error(`GraphQL errors: ${JSON.stringify(payload.errors)}`);

          // null = definition de metaobjet non exposee a la Storefront API : on laisse le repli jouer.
          const connection = payload?.data?.metaobjects;
          if (!connection) return null;

          connection.edges.forEach((edge) => {
            const node = edge?.node;
            if (!node) return;

            const byKey = {};
            (node.fields || []).forEach((field) => byKey[field.key] = field.value);
            models.push({
              id: node.id,
              handle: node.handle,
              nom: byKey.nom || '',
              marque: byKey.marque || '',
            });
          });

          if (!connection.pageInfo?.hasNextPage) break;
          cursor = connection.pageInfo.endCursor;
        }

        return models;
      }

      async fetchModels() {
        try {
          const viaStorefrontAPI = await this.fetchModelsViaStorefrontAPI();
          if (viaStorefrontAPI && viaStorefrontAPI.length) return this.dedupeAndSort(viaStorefrontAPI);
        }
        catch (error) {
          console.warn('[phone-model] Storefront API indisponible, repli sur le rendu Liquid (250 modeles max) :', error);
        }

        return this.dedupeAndSort(await this.fetchModelsFromSections());
      }

      async fetchModelsFromSections() {
        const byPage = new Map();

        // La page courante est rendue inline (attention : sur une URL paginee type ?page=2,
        // c'est la page 2 des modeles qui est inline, d'ou le suivi par numero de page)
        const inline = this.querySelector('[data-phone-models]');
        if (inline) {
          byPage.set(parseInt(inline.getAttribute('data-page')) || 1, JSON.parse(inline.textContent));
        }

        const sectionId = this.getAttribute('data-section-id');
        const missing = [];
        for (let page = 1; page <= this.pages; page++) {
          if (!byPage.has(page)) missing.push(page);
        }

        await Promise.all(missing.map(async (page) => {
          const response = await fetch(`${theme.routes.root_url}?section_id=${encodeURIComponent(sectionId)}&page=${page}`);
          if (!response.ok) throw new Error(`Section render failed (${response.status})`);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const json = doc.querySelector('[data-phone-models]');
          byPage.set(page, json ? JSON.parse(json.textContent) : []);
        }));

        const flat = [];
        [...byPage.keys()].sort((a, b) => a - b).forEach((page) => flat.push(...byPage.get(page)));

        return flat;
      }

      dedupeAndSort(models) {
        const seen = new Set();
        const unique = [];
        models.forEach((model) => {
          if (!model || seen.has(model.handle)) return;
          seen.add(model.handle);
          unique.push(model);
        });

        const collator = new Intl.Collator('fr', { numeric: true, sensitivity: 'base' });
        unique.sort((a, b) => collator.compare(a.marque || '', b.marque || '') || collator.compare(a.nom || '', b.nom || ''));

        return unique;
      }

      /*
       * Modeles proposes par la modale : tout le catalogue, sauf ouverture depuis « Changer » sur
       * une fiche MULTI-MARQUES, ou seuls comptent les modeles du produit. Choisir un modele absent
       * du produit (iPhone 5 sur une coque mat bleue) ne faisait rien : preselectProductVariant ne
       * trouvait aucune option correspondante.
       * Produit d'une seule marque (coque design « Samsung … ») : on ne filtre pas, le client doit
       * pouvoir choisir un autre telephone, model-brand-suggestion propose alors la bonne fiche.
       */
      get visibleModels() {
        if (!this.restrictToProduct || !this.models) return this.models;
        if (document.querySelector('model-brand-suggestion')) return this.models;

        const select = document.querySelector('variant-picker [data-option-slug="modeles-compatibles"] select');
        if (!select) return this.models;

        const values = new Set();
        Array.from(select.options).forEach((option) => {
          const normalized = this.normalize(option.value);
          values.add(normalized);
          if (normalized.includes('/')) values.add(this.normalize(normalized.split('/')[0]));
        });

        const filtered = this.models.filter((model) => {
          const candidates = [this.normalize(model.nom)];
          if (model.marque) candidates.push(this.normalize(model.marque + ' ' + model.nom));
          return candidates.some((candidate) => values.has(candidate));
        });

        // Aucune correspondance (libelles inattendus) : mieux vaut tout le catalogue qu'une liste vide.
        return filtered.length ? filtered : this.models;
      }

      /*
       * Recherche de la modale, mot par mot : chaque mot tape doit commencer un mot de la marque
       * ou du nom du modele, dans n'importe quel ordre (« samsung s25 », « galaxy s25 », « s25 »,
       * « huawei 400 » -> Honor 400 Lite/Pro, la marque d'Honor etant Huawei dans le catalogue).
       * Un mot de 4 caracteres ou plus peut aussi correspondre a la forme sans espaces
       * (« galaxys25 », « iphone15 ») ; pas en dessous, sinon « 5 » remonterait tous les « 15 ».
       */
      searchModels(query, models) {
        const tokens = this.normalize(query).split(/[^a-z0-9]+/).filter(Boolean);
        if (!tokens.length) return models;

        const matches = models.filter((model) => {
          const words = this.normalize(`${model.marque || ''} ${model.nom || ''} ${this.modelDisplayName(model) || ''}`)
            .split(/[^a-z0-9]+/)
            .filter(Boolean);
          const compact = words.join('');
          return tokens.every((token) => words.some((word) => word.startsWith(token))
            || (token.length >= 4 && compact.includes(token)));
        });

        // Modele de base avant ses declinaisons : « iPhone 13 » avant « iPhone 13 Pro Max ».
        return matches.sort((a, b) => this.normalize(a.nom).length - this.normalize(b.nom).length);
      }

      renderList(query = '') {
        if (!this.models) return;

        const q = this.normalize(query);

        // Recherche : liste plate de modeles (transversale a toutes les marques).
        if (q) {
          const items = this.searchModels(q, this.visibleModels);
          this.renderModels(items, { showBrand: true });
          return;
        }

        // Vue "modeles d'une marque" (apres clic sur une marque).
        if (this.currentBrand) {
          const items = this.visibleModels.filter((model) => model.marque === this.currentBrand);
          this.renderModels(items, { showBrand: false, back: true });
          return;
        }

        // Vue principale : les marques.
        this.renderBrands();
      }

      renderBrands() {
        const counts = new Map();
        this.visibleModels.forEach((model) => {
          if (!model.marque) return;
          counts.set(model.marque, (counts.get(model.marque) || 0) + 1);
        });

        const collator = new Intl.Collator('fr', { numeric: true, sensitivity: 'base' });
        const brands = [...counts.keys()].sort((a, b) => collator.compare(a, b));

        this.setStatus(brands.length ? null : 'Aucune marque disponible.');

        this.results.innerHTML = brands.map((brand) => `
          <li role="presentation">
            <button type="button" class="phone-model__option phone-model__brand-option flex items-center justify-between w-full text-left" data-brand="${this.escape(brand)}">
              <span class="phone-model__name font-medium">${this.escape(brand)}</span>
              <span class="phone-model__brand-meta flex items-center gap-2">
                <span class="phone-model__count text-xs text-opacity">${counts.get(brand)}</span>
                <span class="phone-model__chevron" aria-hidden="true">›</span>
              </span>
            </button>
          </li>
        `).join('');
      }

      renderModels(items, { showBrand = false, back = false } = {}) {
        this.setStatus(items.length ? null : 'Aucun modèle trouvé.');

        const selected = this.selection?.handle;
        let html = '';

        if (back && this.currentBrand) {
          html += `
            <li role="presentation">
              <button type="button" class="phone-model__back flex items-center gap-2 w-full text-left" data-back>
                <span class="phone-model__back-arrow" aria-hidden="true">←</span>
                <span class="font-medium">${this.escape(this.currentBrand)}</span>
              </button>
            </li>
          `;
        }

        html += items.map((model) => `
          <li role="presentation">
            <button type="button" role="option" class="phone-model__option flex items-center justify-between w-full text-left" data-handle="${this.escape(model.handle)}" aria-selected="${model.handle === selected}">
              <span class="phone-model__name font-medium">${this.escape(this.modelDisplayName(model) || model.handle)}</span>
              ${showBrand && model.marque && !this.normalize(this.modelDisplayName(model)).startsWith(this.normalize(model.marque)) ? `<span class="phone-model__brand text-xs text-opacity">${this.escape(model.marque)}</span>` : ''}
            </button>
          </li>
        `).join('');

        this.results.innerHTML = html;
      }

      async select(handle) {
        const model = this.models?.find((item) => item.handle === handle);
        if (!model) return;

        this.setCookie(this.cookieName, encodeURIComponent(JSON.stringify(model)), this.expiry);
        this.updateButtons();
        this.renderList(this.input.value);

        // Sur la collection d'un AUTRE modele, filtrer sur place laissait la banniere et le fil
        // d'Ariane de l'ancien modele : on navigue vers la collection du nouveau. AVANT l'event,
        // sinon `collection-brand-suggestion` ouvre sa modale le temps de la navigation.
        const url = await this.modelCollectionRedirectUrl(model);
        if (url) {
          sessionStorage.removeItem(this.optOutKey);
          this.hide();
          window.location.assign(url);
          return;
        }

        document.dispatchEvent(new CustomEvent('phone-model:change', { bubbles: true, detail: { model } }));

        // Choix explicite : on reactive l'auto-filtre et on l'applique tout de suite si on est
        // sur une collection ou la page recherche, et on re-filtre les suggestions predictives
        sessionStorage.removeItem(this.optOutKey);
        this.applyCollectionFilter({ force: true });
        this.filterSearchResults();

        this.hide();
      }

      /*
       * Collection vers laquelle naviguer quand on change de modele depuis une collection de
       * modele, ou null pour rester sur la page (pas sur une collection de modele, ou aucune
       * collection trouvee). Candidats dans l'ordre : les deux conventions de handle de
       * `matchesCollectionModel`, puis la collection marque (modele sans collection dediee).
       * Le filtre modele s'applique a l'arrivee (adoptCollectionModel + applyCollectionFilter).
       */
      async modelCollectionRedirectUrl(model) {
        const current = this.collectionModel;
        if (!current || this.testMode) return null;
        if (this.matchesCollectionModel(model, current)) return null;
        if (!this.cachedModels()?.some((item) => this.matchesCollectionModel(item, current))) return null;

        const candidates = [...new Set([
          `coque-${this.handleizeValue(model.handle)}`,
          `coque-${this.handleizeValue(model.nom)}`,
          model.marque ? `coque-${this.handleizeValue(model.marque)}` : '',
        ])].filter((candidate) => candidate !== 'coque-');

        const root = (window.theme?.routes?.root_url || '/').replace(/\/$/, '');
        for (const candidate of candidates) {
          if (await this.collectionExists(`${root}/collections/${candidate}`, candidate)) {
            return `${root}/collections/${candidate}`;
          }
        }
        return null;
      }

      // HEAD sur la collection, memorise pour la session. Meme cle que
      // `collection-brand-suggestion`, qui verifie les memes handles.
      async collectionExists(url, handle) {
        const cacheKey = `cbs:col:${handle}`;
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached !== null) return cached === '1';
        }
        catch { }

        let ok = false;
        try {
          ok = (await fetch(url, { method: 'HEAD' })).ok;
        }
        catch {
          return false;
        }

        try {
          sessionStorage.setItem(cacheKey, ok ? '1' : '0');
        }
        catch { }
        return ok;
      }

      updateButtons() {
        const model = this.selection;

        document.querySelectorAll('.phone-model-button').forEach((button) => {
          button.classList.toggle('has-model', model !== null);
          button.setAttribute('title', model ? this.modelDisplayName(model) : 'Choisir mon modèle de téléphone');

          const label = button.querySelector('[data-phone-model-label]');
          if (label) {
            label.hidden = model === null;
            label.textContent = model ? this.modelDisplayName(model) : '';
          }
        });
      }

      setStatus(message) {
        this.status.hidden = !message;
        this.status.textContent = message || '';
      }

      normalize(value) {
        return (value || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').toLowerCase().trim();
      }

      escape(value) {
        return (value || '').toString().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      }

      getCookie(name) {
        const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
        return match ? match[2] : null;
      }

      setCookie(name, value, expiryDays) {
        const maxAge = expiryDays > 0 ? `; max-age=${expiryDays * 24 * 60 * 60}` : '';
        document.cookie = `${name}=${value}${maxAge}; path=/; samesite=lax`;
      }
    }
  );
}
